<template>
  <div class="dv2-section">
    <SectionHeader icon="groups" title="Análise dos compradores" anchor="sec-compradores" />

    <div class="dv2-grid dv2-grid--kpi-chart">
      <KpiCard title="Compradores" :columns="2" :loading="loading">
        <KpiTile v-for="tile in buyersKpis" :key="tile.label" v-bind="tile" />
      </KpiCard>

      <HighchartsCard
        title="Histórico dos compradores — valor comprado por mês"
        :tooltip="tooltips.buyersChart"
        :loading="loading"
        :options="buyersOptions"
      />
    </div>

    <div class="dv2-grid dv2-grid--full">
      <TableCard
        title="Ranking de compradores"
        :tooltip="tooltips.buyersRanking"
        :loading="loading"
      >
        <a-table
          :columns="columns"
          :data-source="buyersRanking"
          :pagination="false"
          :row-key="(row) => row.name"
          size="small"
        />
      </TableCard>
    </div>
  </div>
</template>

<script>
import SectionHeader from './SectionHeader.vue';
import KpiCard from './KpiCard.vue';
import KpiTile from './KpiTile.vue';
import HighchartsCard from './HighchartsCard.vue';
import TableCard from './TableCard.vue';
import DashboardV2Service from '@/services/v3/dashboard/DashboardV2Service';
import {
  baseTheme, xAxisDefaults, yAxisDefaults, seriesFromEntities, pivotByCategory,
} from '../shared/highchartsTheme';
import { BRAND } from '../shared/constants';
import {
  brl, pctBR, buildDeltaColumns, formatMonthLabel,
} from '../shared/format';
import { buildKpiTiles, formatKpiValue } from '../shared/kpi';

const service = new DashboardV2Service();

const TOOLTIPS = {
  buyersChart: 'Colunas empilhadas: valor comprado por comprador em cada um dos últimos 13 meses. Linha: média de cotações por comprador ativo.',
  buyersRanking: 'Compradores ordenados por valor comprado, com número de cotações e economia gerada por cada um.',
};

// Estilo por posição para as séries de "Histórico dos compradores": o mock
// manda uma tabela longa (`{ month, buyer, value }`, uma linha por
// combinação) porque o rol de compradores é dado e pode variar; quem decide
// cor/tipo/dashStyle é sempre o componente. As 3 primeiras entidades (top
// compradores) e "Outros compradores" viram colunas empilhadas; a última
// entidade (média por cotação) sempre vira a linha tracejada no eixo direito.
const BUYER_SERIES_STYLES = [
  { color: BRAND.azul },
  { color: BRAND.laranja },
  { color: BRAND.verde },
  { color: '#D8D2CB' },
  {
    type: 'spline', yAxis: 1, color: BRAND.preto, lineWidth: 2, dashStyle: 'ShortDash', markerRadius: 3,
  },
];

const BUYERS_STYLE = {
  activeInPeriod: {
    label: 'Ativos no período',
    tooltip: 'Compradores que conduziram ao menos uma cotação no período.',
    format: 'int', valueColor: BRAND.preto, captionColor: BRAND.muted,
    captionTemplate: (raw) => `de ${raw.totalRegistered} cadastrados`,
  },
  quotesPerBuyer: {
    label: 'Cotações por comprador',
    tooltip: 'Média de cotações conduzidas por comprador ativo no período.',
    format: 'decimal1', valueColor: BRAND.laranja, captionColor: BRAND.laranja,
    deltaMonthFormat: 'decimal2', deltaYearFormat: 'decimal2',
    captionTemplate: (raw) => `${raw.totalQuotes} cotações em ${raw.totalBuyers} compradores`,
  },
  savingsPerBuyer: {
    label: 'Economia por comprador',
    tooltip: 'Economia média gerada por comprador ativo no período.',
    format: 'currencyMil', valueColor: BRAND.verde, captionColor: BRAND.verde,
    captionTemplate: (raw) => `${formatKpiValue(raw.totalSavings, 'currencyMil')} no total`,
  },
  avgClosingDays: {
    label: 'Prazo médio de fechamento',
    tooltip: 'Dias médios entre a abertura da cotação e a decisão de compra.',
    format: 'daysDecimal1', valueColor: BRAND.azul, captionColor: BRAND.azul,
    deltaMonthFormat: 'decimal2', deltaYearFormat: 'decimal2',
    // menos dias para fechar é uma melhora, por isso diminuir é "bom" aqui
    goodDirection: 'down',
  },
};

export default {
  name: 'BuyersSection',

  components: {
    SectionHeader, KpiCard, KpiTile, HighchartsCard, TableCard,
  },

  props: {
    filters: { type: Object, default: () => ({}) },
  },

  data() {
    return {
      loading: true,
      tooltips: TOOLTIPS,
      buyersKpis: [],
      buyersChart: null,
      buyersRanking: [],
    };
  },

  computed: {
    buyersOptions() {
      if (!this.buyersChart) return {};
      const { categories, series } = pivotByCategory(this.buyersChart, { seriesKey: 'buyer' });
      const theme = baseTheme();

      return {
        ...theme,
        chart: { ...theme.chart, type: 'column' },
        xAxis: xAxisDefaults({ categories: categories.map(formatMonthLabel), crosshair: true }),
        yAxis: [
          yAxisDefaults({
            title: { text: 'Valor comprado (R$)' },
            labels: { style: { fontSize: '11px', color: BRAND.cinza }, formatter() { return `R$ ${Math.round(this.value / 1000)}k`; } },
          }),
          yAxisDefaults({ opposite: true, gridLineWidth: 0, min: 0, title: { text: 'Cotações por comprador' } }),
        ],
        tooltip: { ...theme.tooltip, shared: true },
        plotOptions: { ...theme.plotOptions, column: { stacking: 'normal', borderRadius: 2, groupPadding: 0.08 } },
        series: seriesFromEntities(series, BUYER_SERIES_STYLES),
      };
    },

    columns() {
      const h = this.$createElement;
      const rankColumn = {
        title: '#',
        align: 'center',
        width: 44,
        customRender: (_, __, index) => h('span', { style: { fontWeight: 700, color: BRAND.cinza } }, index + 1),
      };

      const base = [
        rankColumn,
        {
          title: 'Comprador',
          dataIndex: 'name',
          customRender: (text) => h('span', { style: { fontWeight: 700 } }, text),
        },
        {
          title: 'Cotações',
          dataIndex: 'quotes',
          align: 'right',
          sorter: (a, b) => a.quotes - b.quotes,
        },
        {
          title: 'Valor comprado',
          dataIndex: 'approved',
          align: 'right',
          customRender: (v) => brl(v),
        },
        {
          title: 'Economia',
          dataIndex: 'savings',
          align: 'right',
          customRender: (v) => brl(v),
        },
        {
          title: '% econ.',
          dataIndex: 'pct',
          align: 'right',
          customRender: (v) => h('a-tag', { props: { color: 'green' } }, pctBR(v)),
        },
      ];

      return base.concat(buildDeltaColumns(h));
    },
  },

  watch: {
    filters: {
      deep: true,
      handler() {
        this.fetch();
      },
    },
  },

  created() {
    this.fetch();
  },

  methods: {
    fetch() {
      this.loading = true;
      service.getCompradores(this.filters).then(({ data }) => {
        this.buyersKpis = buildKpiTiles(data.kpis, BUYERS_STYLE, { valueFontSize: '34px' });
        this.buyersChart = data.historic;
        this.buyersRanking = data.ranking;
        this.loading = false;
      });
    },
  },
};
</script>
